import React from "react";
import { useShow } from "@refinedev/core";
import {
  Tag,
  Card,
  Row,
  Col,
  Descriptions,
  Table,
  Spin,
  Alert,
  Typography,
  Button
} from "antd";
import { CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PROCUREMENT_PLAN_REPORT_ONE_QUERY } from "./queries";
import { IProcurementPlanReport } from "./interface";
import WorkflowDiagram from "../purchase-template/components/WorkflowDiagram";
import { SubmitButton } from "./components/SubmitButton";
import { UserInfo } from "../../components/UserInfo";

const { Text } = Typography;

// Status badges for procurement plan reports
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusColors: Record<string, string> = {
    DRAFT: 'default',
    SUBMITTED: 'processing',
    APPROVED: 'success',
    REJECTED: 'error'
  };

  const statusText: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected'
  };

  return (
    <Tag color={statusColors[status] || 'default'}>
      {statusText[status] || status}
    </Tag>
  );
};

export const ShowProcurementPlanReport: React.FC = () => {
  const { query } = useShow<IProcurementPlanReport>({
    resource: "procurementPlanReports",
    meta: {
      gqlQuery: PROCUREMENT_PLAN_REPORT_ONE_QUERY,
    },
  });
  const { data, isLoading } = query;
  const record = data?.data;

  const handleSubmitSuccess = () => {
    // Refresh the data after successful submission
    query.refetch();
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (!record) {
    return <div>Procurement plan report not found</div>;
  }

  // Columns for the plans table
  const planColumns = [
    {
      title: 'Team',
      dataIndex: 'teamName',
      key: 'teamName',
    },
    {
      title: 'Plan ID',
      dataIndex: 'procurementPlanId',
      key: 'procurementPlanId',
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `ETB ${amount.toLocaleString()}`
    },
    {
      title: 'Item Count',
      dataIndex: 'itemCount',
      key: 'itemCount',
    },
    {
      title: 'Captured At',
      dataIndex: 'capturedAt',
      key: 'capturedAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'Plan Submission Date',
      dataIndex: 'planSubmissionDate',
      key: 'planSubmissionDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
  ];

  // Columns for items table (expanded view)
  const itemColumns = [
    {
      title: 'Item Name',
      dataIndex: 'productName',
      key: 'productName',
      render: (productName: any) => productName || '-'
    },
    {
      title: 'Description',
      dataIndex: 'productDescription',
      key: 'productDescription',
      render: (productDescription: any) => productDescription || '-'
    },
    {
      title: 'Category',
      dataIndex: 'productCategory',
      key: 'productCategory',
      render: (productCategory: any) => productCategory || '-'
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      render: (unit: any) => unit || '-'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: any) => quantity || 0
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (unitPrice: any) => unitPrice ? `ETB ${unitPrice.toLocaleString()}` : 'ETB 0'
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (totalPrice: any) => totalPrice ? `ETB ${totalPrice.toLocaleString()}` : 'ETB 0'
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: any) => priority || '-'
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      key: 'requiredDate',
      render: (requiredDate: any) => requiredDate ? dayjs(requiredDate).format('YYYY-MM-DD') : '-'
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Procurement Plan Report Details</h1>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        {record.status === 'DRAFT' && (
          <div style={{ display: 'inline-block', marginRight: 8 }}>
            <SubmitButton
              reportId={record.id}
              report={record}
              buttonText="Submit"
              onSuccess={handleSubmitSuccess}
            />
          </div>
        )}
        {record.status === 'SUBMITTED' && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            disabled
          >
            Submitted for Approval
          </Button>
        )}
        {(record.status === 'APPROVED' || record.status === 'REJECTED') && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            disabled
          >
            {record.status === 'APPROVED' ? 'Approved' : 'Rejected'}
          </Button>
        )}
      </div>
      
      {record.isOutdated && (
        <Alert
          message="This report is outdated"
          description="The data in this report may not reflect the current state of procurement plans."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Report Information">
               <Descriptions column={2} layout="vertical" bordered>
               <Descriptions.Item label="Year">
                 {record.year}
               </Descriptions.Item>
               <Descriptions.Item label="Status">
                 <StatusBadge status={record.status} />
               </Descriptions.Item>
               <Descriptions.Item label="Created By">
                 {record.createdBy ? <UserInfo userId={record.createdBy} /> : '-'}
               </Descriptions.Item>
               <Descriptions.Item label="Updated By">
                 {record.updatedBy ? <UserInfo userId={record.updatedBy} /> : '-'}
               </Descriptions.Item>
               <Descriptions.Item label="Total Amount">
                 ETB {record.totalAmount.toLocaleString()}
               </Descriptions.Item>
               <Descriptions.Item label="Currency">
                 {record.currency}
               </Descriptions.Item>
               <Descriptions.Item label="Approval Type">
                 {record.approvalType}
               </Descriptions.Item>
               <Descriptions.Item label="Template">
                 {record.templateId || 'None'}
               </Descriptions.Item>
               <Descriptions.Item label="Created At">
                 {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}
               </Descriptions.Item>
               <Descriptions.Item label="Updated At">
                 {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm')}
               </Descriptions.Item>
               <Descriptions.Item label="Submitted By">
                 {record.submittedBy ? <UserInfo userId={record.submittedBy} /> : '-'}
               </Descriptions.Item>
               <Descriptions.Item label="Submission Date">
                 {record.submissionDate ? dayjs(record.submissionDate).format('YYYY-MM-DD HH:mm') : '-'}
               </Descriptions.Item>
             </Descriptions>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card title="Procurement Plans Summary">
            <Table
              dataSource={record.plans}
              columns={planColumns}
              rowKey="procurementPlanId"
              pagination={false}
              expandable={{
                defaultExpandAllRows: true,
                expandedRowRender: (plan) => (
                  <div style={{ margin: 0 }}>
                    <Text strong>Items:</Text>
                    <Table
                      dataSource={plan.items || []}
                      columns={itemColumns}
                      rowKey={(item) => `${plan.procurementPlanId}-${item.productName}`}
                      pagination={false}
                      size="small"
                    />
                  </div>
                ),
                rowExpandable: (plan) => Boolean(plan.items && plan.items.length > 0)
              }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <strong>ETB {record.plans.reduce((sum: number, plan: any) => sum + plan.totalAmount, 0).toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <strong>{record.plans.reduce((sum: number, plan: any) => sum + (plan.itemCount || 0), 0)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} colSpan={2}></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Approval Information */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title="Approval Information" bordered>
            {record.approval ? (
              <Descriptions column={2} layout="vertical" bordered>
                 <Descriptions.Item label="Approved By">
                  {record.approval.approvedBy ? (
                    <UserInfo userId={record.approval.approvedBy} />
                  ) : (
                    'N/A'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Approved On">
                  {record.approval.approvedOn ? dayjs(record.approval.approvedOn).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Remarks" span={2}>
                  {record.approval.remarks || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <div>No approval data available.</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Approval Workflow Diagram */}
      {record.approvalType === 'EXTERNAL' && record.externalWorkflow && (
        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card title="Approval Workflow">
              <WorkflowDiagram flow={record.externalWorkflow} />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};